import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import styles from './AddTeamMemberModal.module.css';

interface AddTeamMemberModalProps {
    isOpen: boolean;
    onClose: () => void;
    onMemberAdded: () => void;
    orgId: string;
}

const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
    isOpen,
    onClose,
    onMemberAdded,
    orgId
}) => {
    const [nickname, setNickname] = useState('');
    const [email, setEmail] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!nickname.trim()) {
            setError('Nickname is required');
            return;
        }

        if (!email.trim()) {
            setError('Email is required');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setError('Please enter a valid email address');
            return;
        }

        setIsAdding(true);
        setError(null);

        try {
            const { getUserByEmail, addTeamMember } = await import('../utils/subscription');
            
            // First, get the user by email
            const userData = await getUserByEmail(email.trim());
            
            // Then add the user to the organization (always as non-admin)
            await addTeamMember(orgId, userData.user_id, false, nickname.trim());
            
            onMemberAdded();
            setNickname('');
            setEmail('');
            onClose();
        } catch (error: any) {
            console.error('Error adding team member:', error);
            
            // Handle specific error cases
            if (error.message) {
                // Check for common error patterns
                if (error.message.includes('already in an organization')) {
                    setError('This user is already a member of another team.');
                } else if (error.message.includes('No user with email')) {
                    setError(error.message);
                } else if (error.message.includes('403')) {
                    setError('You do not have permission to add team members.');
                } else if (error.message.includes('401')) {
                    setError('Please log in again to continue.');
                } else {
                    setError(error.message);
                }
            } else {
                setError('Failed to add team member. Please try again.');
            }
        } finally {
            setIsAdding(false);
        }
    };

    const handleClose = () => {
        if (!isAdding) {
            setNickname('');
            setEmail('');
            setError(null);
            onClose();
        }
    };

    return (
        <Modal show={isOpen} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Team Member</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className={styles.infoAlert}>
                    This is not an invitation system.<br />
                    The user needs to create a free account first.
                </Alert>
                
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}
                
                <Form onSubmit={handleSubmit}>
                    <Form.Group className={styles.formGroup}>
                        <Form.Label className={styles.formLabel}>Nickname</Form.Label>
                        <Form.Control
                            type="text"
                            value={nickname}
                            onChange={(e) => setNickname(e.target.value)}
                            disabled={isAdding}
                            autoFocus
                            className={styles.formControl}
                        />
                    </Form.Group>
                    
                    <Form.Group className={styles.formGroup}>
                        <Form.Label className={styles.formLabel}>Email Address</Form.Label>
                        <Form.Control
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isAdding}
                            className={styles.formControl}
                        />
                        <Form.Text className={styles.helpText}>
                            The user must have an <span className={styles.warningText}>existing account</span> with this email address.
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className={styles.modalFooter}>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isAdding}
                    className={styles.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isAdding || !nickname.trim() || !email.trim()}
                    className={styles.submitButton}
                >
                    {isAdding ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Adding...
                        </>
                    ) : (
                        'Add Member'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddTeamMemberModal; 